import { Minus, Plus, Trash2 } from 'lucide-react';

import { COMMERCE } from '@/config/site';
import { cn } from '@/lib/cn';

interface QuantityStepperProps {
  value: number;
  onChange: (value: number) => void;
  /** Transforma o botão de diminuir em remover quando a quantidade é 1. */
  removable?: boolean;
  onRemove?: () => void;
  /** Teto próprio. Sem isso vale `COMMERCE.maxQuantityPerItem`. */
  max?: number;
  size?: 'sm' | 'md';
  label?: string;
  className?: string;
}

export function QuantityStepper({
  value,
  onChange,
  removable = false,
  onRemove,
  max,
  size = 'md',
  label = 'Quantidade',
  className,
}: QuantityStepperProps) {
  const ceiling = Math.min(max ?? COMMERCE.maxQuantityPerItem, COMMERCE.maxQuantityPerItem);
  const atMin = value <= 1;
  const atMax = value >= ceiling;
  const showRemove = removable && atMin;

  const button = cn(
    'grid place-items-center rounded-lg text-brown-800 transition-colors',
    'hover:bg-cream-300 disabled:opacity-30 disabled:hover:bg-transparent',
    size === 'sm' ? 'size-8' : 'size-10',
  );

  return (
    <div className={cn('inline-flex flex-col gap-1', className)}>
      <div className="inline-flex items-center gap-1 rounded-xl border-2 border-sand-300 bg-white p-1">
        <button
          type="button"
          className={button}
          onClick={() => (showRemove ? onRemove?.() : onChange(value - 1))}
          disabled={atMin && !showRemove}
          aria-label={showRemove ? 'Remover item' : 'Diminuir quantidade'}
        >
          {showRemove ? (
            <Trash2 className="size-4 text-danger-500" aria-hidden="true" />
          ) : (
            <Minus className="size-4" aria-hidden="true" />
          )}
        </button>

        <output
          aria-live="polite"
          aria-label={label}
          className={cn(
            'text-center font-display font-bold text-brown-900 tabular-nums',
            size === 'sm' ? 'w-7 text-sm' : 'w-9 text-base',
          )}
        >
          {value}
        </output>

        <button
          type="button"
          className={button}
          onClick={() => onChange(value + 1)}
          disabled={atMax}
          aria-label="Aumentar quantidade"
        >
          <Plus className="size-4" aria-hidden="true" />
        </button>
      </div>

      {/* Um botão que apenas apaga não explica nada — diz o porquê do limite. */}
      {atMax && (
        <span className="text-[11px] leading-none text-brown-700/80">
          Máximo de {ceiling} por item
        </span>
      )}
    </div>
  );
}
